import { i18nComponents } from "@/components/i18n";
import {
	type FilterByPrefix,
	filterKeysByPrefix,
	typedObjectKeys,
} from "@/lib/utils";
import { m } from "@/paraglide/messages";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@bidbattles/bloks/components/ui/accordion";
import { Divider } from "@bidbattles/bloks/components/ui/divider";
import { Input } from "@bidbattles/bloks/components/ui/input";
import { Link } from "@bidbattles/bloks/components/ui/link";
import { paginationSearchParamsSchema } from "@bidbattles/bloks/lib/rapace/utils";
import { cn } from "@bidbattles/bloks/lib/ui";
import {
	faDiscord,
	faFacebookF,
	faInstagram,
} from "@fortawesome/free-brands-svg-icons";
import {
	type IconDefinition,
	faBoxOpen,
	faCheckCircle,
	faClock,
	faCreditCard,
	faEnvelope,
	faFileContract,
	faGavel,
	faHeadset,
	faInfinity,
	faInfoCircle,
	faPhone,
	faRightLeft,
	faRocket,
	faRotateLeft,
	faShieldHalved,
	faTimesCircle,
	faTriangleExclamation,
	faTruckFast,
	faUserCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ParaglideMessage } from "@inlang/paraglide-js-react";
import { useDebouncedValue } from "@tanstack/react-pacer/debouncer";
import { createFileRoute } from "@tanstack/react-router";
import { SearchMd } from "@untitledui/icons";
import { zip, zipObject } from "es-toolkit";
import { useMemo } from "react";
import { Fragment } from "react/jsx-runtime";

export const Route = createFileRoute("/help-center")({
	validateSearch: paginationSearchParamsSchema.pick({ query: true }),
	component: RouteComponent,
});

const entryPriceRefundMessages = [
	[
		faCheckCircle,
		m.help_center__refund_policy__entry_price__before_start,
	] as const,
	[
		faTimesCircle,
		m.help_center__refund_policy__entry_price__after_start,
	] as const,
	[
		faInfoCircle,
		m.help_center__refund_policy__entry_price__battle_cancel,
	] as const,
] as const;

const bidPackageRefundMessages = [
	[
		faTimesCircle,
		m.help_center__refund_policy__bid_package__no_refunds,
	] as const,
	[
		faRightLeft,
		m.help_center__refund_policy__bid_package__no_exchanges,
	] as const,
	[
		faInfinity,
		m.help_center__refund_policy__bid_package__never_expire,
	] as const,
] as const;

const returnsMessages = [
	[
		faShieldHalved,
		m.help_center__refund_policy__returns__legal_warranty,
	] as const,
	[
		faBoxOpen,
		m.help_center__refund_policy__returns__defective_products,
	] as const,
	[
		faRotateLeft,
		m.help_center__refund_policy__returns__cooloff_period,
	] as const,
	[
		faTriangleExclamation,
		m.help_center__refund_policy__returns__important,
	] as const,
] as const;

const refundsMessages = [
	[faClock, m.help_center__refund_policy__refunds__processing_time] as const,
	[faEnvelope, m.help_center__refund_policy__refunds__confirmation] as const,
] as const;

const refundPolicySections = [
	[
		m.help_center__refund_policy__entry_price__title,
		entryPriceRefundMessages,
	] as const,
	[
		m.help_center__refund_policy__bid_package__title,
		bidPackageRefundMessages,
	] as const,
	[m.help_center__refund_policy__returns__title, returnsMessages] as const,
	[m.help_center__refund_policy__refunds__title, refundsMessages] as const,
] as const;

function questionsAndAnswers<
	Keys extends string[],
	QuestionPrefix extends string,
	AnswerPrefix extends string,
>(
	keys: Keys,
	qPrefix: QuestionPrefix,
	aPrefix: AnswerPrefix,
): [
	FilterByPrefix<Keys, QuestionPrefix>,
	FilterByPrefix<Keys, AnswerPrefix>,
][] {
	const questions = filterKeysByPrefix(keys, qPrefix);
	const answers = filterKeysByPrefix(keys, aPrefix);
	return zip(questions, answers);
}

const keys = typedObjectKeys(m);
const helpCenterData = [
	{
		id: "getting-started",
		icon: faRocket,
		name: m.help_center__getting_started__name,
		description: m.help_center__getting_started__description,
		questions: questionsAndAnswers(
			keys,
			"help_center__getting_started__question_",
			"help_center__getting_started__answer_",
		),
	},
	{
		id: "bidding-n-battles",
		icon: faGavel,
		name: m.help_center__bidding_n_battles__name,
		description: m.help_center__bidding_n_battles__description,
		questions: questionsAndAnswers(
			keys,
			"help_center__bidding_n_battles__question_",
			"help_center__bidding_n_battles__answer_",
		),
	},
	{
		id: "payments-n-refunds",
		icon: faCreditCard,
		name: m.help_center__payments_n_refunds__name,
		description: m.help_center__payments_n_refunds__description,
		questions: questionsAndAnswers(
			keys,
			"help_center__payments_n_refunds__question_",
			"help_center__payments_n_refunds__answer_",
		),
	},
	{
		id: "shipping-n-delivery",
		icon: faTruckFast,
		name: m.help_center__shipping_n_delivery__name,
		description: m.help_center__shipping_n_delivery__description,
		questions: questionsAndAnswers(
			keys,
			"help_center__shipping_n_delivery__question_",
			"help_center__shipping_n_delivery__answer_",
		),
	},
	{
		id: "account-n-technical",
		icon: faUserCircle,
		name: m.help_center__account_n_technical__name,
		description: m.help_center__account_n_technical__description,
		questions: questionsAndAnswers(
			keys,
			"help_center__account_n_technical__question_",
			"help_center__account_n_technical__answer_",
		),
	},
];

function RouteComponent() {
	const navigate = Route.useNavigate();
	const { query } = Route.useSearch();
	const [debouncedQuery] = useDebouncedValue(query, {
		wait: 10,
	});
	const needle = useMemo(
		() => debouncedQuery?.trim().toLowerCase() || "",
		[debouncedQuery],
	);

	const matchingResults = useMemo(() => {
		if (!needle) {
			return helpCenterData;
		}

		let matching: string[] = [];
		return helpCenterData
			.filter((category) => {
				const matchingQuestions = category.questions.filter(
					([q, a]) =>
						m[q]().toLowerCase().includes(needle) ||
						m[a]().toLowerCase().includes(needle),
				);
				if (matchingQuestions.length > 0) {
					matching = [...matching, ...matchingQuestions.map(([q]) => q)];
				}
				return (
					category.name().toLowerCase().includes(needle) ||
					category.description().toLowerCase().includes(needle) ||
					matchingQuestions.length > 0
				);
			})
			.map((category) => ({
				...category,
				questions: category.questions.filter(([q]) => matching.includes(q)),
			}));
	}, [needle]);

	return (
		<main className="flex flex-col gap-y-8 items-stretch grow h-full">
			<section className="p-box flex flex-col items-center gap-y-12 md:gap-y-16 mx-auto">
				<div className="flex flex-col gap-y-3 text-center max-w-100 md:max-w-180">
					<h1 className="text-gray-900 font-bold text-5xl leading-11 md:text-6xl md:leading-22.5">
						Help Center
					</h1>
					<p className="text-gray-500 text-lg leading-7 mb-6">
						Find answers to your questions about BidBattles
					</p>
					<Input
						className="max-w-99 bg-gray-warm-100 border-gray-warm-100"
						variant="roundedBox"
						prefix={<SearchMd size="1em" color="currentColor" />}
						value={query || ""}
						onChange={(e) => {
							navigate({
								to: ".",
								search: { query: e.target.value },
								replace: true,
							});
						}}
					/>
				</div>
			</section>
			<section className="flex flex-col items-center bg-gray-warm-100 gap-12 py-box">
				<div className="px-box max-w-container flex flex-col md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 max">
					{matchingResults.map((category) => (
						<HCCategoryCard
							key={category.id}
							id={category.id}
							icon={
								<FontAwesomeIcon icon={category.icon} color="currentColor" />
							}
							name={category.name()}
							description={category.description()}
						/>
					))}
				</div>
				{!needle && (
					<div className="w-full max-w-container px-box">
						<div className="rounded-2xl bg-white w-full max-w-container text-gray-500">
							<div className="px-4 md:px-8 py-6 border-b border-gray-warm-200">
								<h2 className="font-bold text-gray-900 inline-flex items-center gap-2 text-2xl md:text-3xl">
									<FontAwesomeIcon icon={faFileContract} color="currentColor" />
									{m.help_center__refund_policy__title()}
								</h2>
							</div>
							<div className="px-4 md:px-8 p-8">
								{refundPolicySections.map(([name, messages], index) => (
									<Fragment key={`section-${name.name}`}>
										<div className="md:px-6">
											<h3 className="font-bold text-xl md:text-2xl text-gray-900 mb-4">
												{name()}
											</h3>
											<ul className="flex flex-col gap-4">
												{messages.map(([icon, message]) => (
													<li
														key={`message-${message.name}`}
														className="inline-flex items-start gap-2"
													>
														<FontAwesomeIcon
															icon={icon}
															color="currentColor"
															className="text-gray-500 text-sm mt-1"
														/>
														<p>
															<ParaglideMessage
																message={message}
																markup={{
																	strong: ({ children, attributes }) => (
																		<i18nComponents.Strong {...attributes}>
																			{children}
																		</i18nComponents.Strong>
																	),
																	br: () => <br />,
																}}
															/>
														</p>
													</li>
												))}
											</ul>
										</div>
										{index < refundPolicySections.length - 1 && (
											<Divider className="bg-gray-warm-200 border-gray-warm-200 my-6" />
										)}
									</Fragment>
								))}
							</div>
						</div>
					</div>
				)}

				{matchingResults.length === 0 && (
					<p className="text-gray-500">
						No results found for "{debouncedQuery}".
					</p>
				)}
				{matchingResults.map((category) => (
					<div key={category.id} className="w-full max-w-container px-box">
						<div className="rounded-2xl bg-white w-full text-gray-500">
							<div className="px-4 md:px-8 py-6">
								<h2
									id={`category-${category.id}`}
									className="font-bold text-gray-900 text-2xl md:text-3xl inline-flex items-center gap-3"
								>
									<FontAwesomeIcon icon={category.icon} color="currentColor" />
									{category.name()}
								</h2>
							</div>
							<Accordion
								multiple={!!needle}
								value={needle ? category.questions.map(([q]) => q) : undefined}
								className="p-4 md:px-8"
							>
								{category.questions.map(([question, answer]) => (
									<AccordionItem
										key={question}
										className="border-gray-warm-200"
										value={question}
									>
										<AccordionTrigger className="text-gray-900 font-bold md:text-lg">
											{m[question]()}
										</AccordionTrigger>
										<AccordionContent className="whitespace-pre-line">
											<ParaglideMessage
												message={m[answer]}
												markup={{
													strong: ({ children, attributes }) => (
														<i18nComponents.Strong {...attributes}>
															{children}
														</i18nComponents.Strong>
													),
													br: () => <br />,
												}}
											/>
										</AccordionContent>
									</AccordionItem>
								))}
							</Accordion>
						</div>
					</div>
				))}
				<StillNeedHelp />
			</section>
		</main>
	);
}

function StillNeedHelp() {
	return (
		<section
			aria-labelledby="support-heading"
			className="w-full max-w-container px-box"
		>
			<div className="w-full rounded-2xl bg-white p-4 md:p-8">
				<h2
					id="support-heading"
					className="flex items-center gap-3 text-2xl font-bold text-gray-900 md:text-3xl"
				>
					<FontAwesomeIcon aria-hidden icon={faHeadset} />
					Still Need Help?
				</h2>
				<p className="mt-12 text-center text-gray-500 md:text-lg">
					Our support team is here to help you with any questions or issues.
				</p>
				<div className="mx-auto mt-7 grid w-full max-w-2xl gap-5 md:grid-cols-2">
					<div className="flex flex-col items-center rounded-xl border border-gray-200 p-6 text-center">
						<FontAwesomeIcon
							aria-hidden
							icon={faEnvelope}
							className="text-5xl text-gray-900"
						/>
						<h3 className="mt-4 text-xl font-bold text-gray-900">
							Email Support
						</h3>
						<p className="mt-1 text-gray-500">Response within 24 hours</p>
						<a
							href="mailto:support@bidbattles.com"
							className="mt-4 inline-flex min-h-10 items-center justify-center rounded-full bg-accent-primary px-5 font-bold text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent-primary"
						>
							support@bidbattles.com
						</a>
					</div>
					<div className="flex flex-col items-center rounded-xl border border-gray-200 p-6 text-center">
						<FontAwesomeIcon
							aria-hidden
							icon={faPhone}
							className="text-5xl text-gray-900"
						/>
						<h3 className="mt-4 text-xl font-bold text-gray-900">
							Phone Support
						</h3>
						<p className="mt-1 text-gray-500">Mon-Fri, 9am-6pm CET</p>
						<a
							href="tel:+31201234567"
							className="mt-4 inline-flex min-h-10 items-center justify-center rounded-full bg-accent-primary px-5 font-bold text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent-primary"
						>
							+31 20 123 4567
						</a>
					</div>
				</div>
				<div className="mt-12 text-center">
					<h3 className="text-2xl font-bold text-gray-900">
						Join Our Community
					</h3>
					<p className="mt-3 text-gray-500">
						Connect with other BidBattles players, share strategies, and get
						tips from our community!
					</p>
					<div className="mt-5 flex flex-wrap justify-center gap-3">
						<span className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-accent-primary px-5 font-bold text-accent-primary">
							<FontAwesomeIcon
								aria-hidden
								icon={faFacebookF}
								className="size-4"
							/>
							Facebook
						</span>
						<span className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-accent-primary px-5 font-bold text-accent-primary">
							<FontAwesomeIcon
								aria-hidden
								icon={faInstagram}
								className="size-4"
							/>
							Instagram
						</span>
						<span className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-accent-primary px-5 font-bold text-accent-primary">
							<FontAwesomeIcon
								aria-hidden
								icon={faDiscord}
								className="size-4"
							/>
							Discord
						</span>
					</div>
				</div>
			</div>
		</section>
	);
}

function HCCategoryCard({
	className,
	id,
	icon,
	name,
	description,
}: {
	icon: React.ReactElement;
	id: string;
	name: string;
	description: string;
	className?: string;
}) {
	return (
		<Link
			to="."
			hash={`category-${id}`}
			className={cn("p-4 bg-white rounded-xl flex flex-col gap-4", className)}
		>
			<div className="flex items-center gap-4">
				<div className="size-14 rounded-full flex items-center justify-center bg-gray-500 text-white text-2xl">
					{icon}
				</div>
				<p className="font-bold text-xl text-gray-900">{name}</p>
			</div>
			<p className="text-gray-500">{description}</p>
		</Link>
	);
}
